import {
  CheckStatus,
  ComparisonBenefit,
  EngineDisagreement,
  IBenefitVerdict,
  IVerdictOption,
  ISourceAgreementCell,
  IVerificationCell,
  IVerificationTotals,
  IPendingConfigAccount,
  IWorklistRow,
  TruthSource,
  VerificationStatus,
  WorklistState,
} from "types/BenefitComparison/BenefitComparison";

import { formatRemitValue, relativeTime } from "./remitAccuracyWording";

/**
 * CLE-2820 — every label on the Engine worklist, in one place so the tests can pin them.
 * The UI only ever says "AI".
 *
 * 🚨 EVERY LABEL NAMES ITS ACTUAL SOURCE. An earlier draft bundled remits and verdicts as "truth"
 * and legacy and the AI as "witnesses". Nothing here may reintroduce a collective noun.
 */

export const COMPARISON_BENEFITS: ComparisonBenefit[] = [
  "COINSURANCE_PERCENT",
  "FLAT_COPAY",
  "PROFESSIONAL_COPAY",
  "REMAINING_DEDUCTIBLE",
  "REMAINING_OOP",
];

export const COMPARISON_BENEFIT_LABELS: Record<ComparisonBenefit, string> = {
  COINSURANCE_PERCENT: "Co-insurance",
  FLAT_COPAY: "Copay",
  PROFESSIONAL_COPAY: "Physician copay",
  REMAINING_DEDUCTIBLE: "Remaining deductible",
  REMAINING_OOP: "Remaining out-of-pocket",
};

/**
 * 🚨 The scorecard says "Deductible" and "Out-of-pocket" where the worklist's Benefit column says
 * "Remaining deductible" and "Remaining out-of-pocket". That is deliberate and comes from the
 * mockups: the card is five labels read as a set against one another, where the shared word is
 * noise, and the column is one benefit read on its own, where it is the meaning.
 */
export const SCORECARD_BENEFIT_LABELS: Record<ComparisonBenefit, string> = {
  ...COMPARISON_BENEFIT_LABELS,
  REMAINING_DEDUCTIBLE: "Deductible",
  REMAINING_OOP: "Out-of-pocket",
};

/**
 * The five benefits in a FIXED order — deductible, out-of-pocket, co-insurance, copay, physician
 * copay — so the same benefit is in the same place every time somebody looks.
 */
export const SCORECARD_BENEFIT_ORDER: ComparisonBenefit[] = [
  "REMAINING_DEDUCTIBLE",
  "REMAINING_OOP",
  "COINSURANCE_PERCENT",
  "FLAT_COPAY",
  "PROFESSIONAL_COPAY",
];

/**
 * 🚨 THE ENGINE ACCURACY CARD SHOWS ONLY WHAT A REMIT CAN SCORE — three benefits, not five.
 *
 * A remit states what the patient owed on ONE CLAIM; the eligibility check states what deductible or
 * out-of-pocket they had REMAINING at check time. A claim consuming $200 of deductible is consistent
 * with any remaining balance at or above $200, so the two are not comparable and the API's
 * `REMIT_SCORED_BENEFITS` has never held them. Verified on prod 2026-09-21:
 * `BenefitVerificationStatus` holds 1,498 copay / 841 co-insurance / 255 physician copay rows and
 * ZERO for these two, every one remit-sourced (there is 1 human verdict in the database).
 *
 * They used to sit here reading "—%", which invited the reader to wait for a number that is never
 * coming. They are on the agreement cards below instead, which need no truth source at all.
 *
 * ⚠️ KEEP THIS IN STEP WITH `REMIT_SCORED_BENEFITS` on the API. If a remit ever becomes able to score
 * another benefit, it belongs in both.
 */
export const ACCURACY_BENEFIT_ORDER: ComparisonBenefit[] = [
  "COINSURANCE_PERCENT",
  "FLAT_COPAY",
  "PROFESSIONAL_COPAY",
];

/** Dollars, or a whole percent for co-insurance; "no value" is the caller's to render. */
export const formatComparisonValue = (
  value: number | null | undefined,
  benefit: ComparisonBenefit
) =>
  formatRemitValue(
    value,
    benefit === "COINSURANCE_PERCENT" ? "COINSURANCE_PERCENT" : "FLAT_COPAY"
  );

/** "$250" or "$150 – $300"; an empty string where nothing has a value. */
export const rangeText = (
  min: number | null,
  max: number | null,
  benefit: ComparisonBenefit | null
) => {
  if (min === null || max === null || benefit === null) return "";
  const low = formatComparisonValue(min, benefit);
  return min === max ? low : `${low} – ${formatComparisonValue(max, benefit)}`;
};

// ---------------------------------------------------------------------------
// The worklist
// ---------------------------------------------------------------------------

/**
 * 🚨 THE UI SAYS "HUMAN INPUT", THE DATABASE AND API SAY "VERDICT". A reviewer is recording what the
 * value should have been, and "verdict" read as a ruling on which method won — which is precisely
 * what this never records. The model, the tables, the endpoints and `IBenefitVerdict` keep their
 * names; only what a person reads changes, the same split already used for "AI" over the vendor's.
 */
/**
 * 🚨 THE RANK ORDER, WHICH IS ALSO THE DROPDOWN ORDER. It mirrors `WORKLIST_STATES` on the API, where
 * the array's order IS `STATE_RANK` — a regression outranks a confirmed disagreement, which outranks
 * an unconfirmed lead, which outranks a configuration gap. Sorting these alphabetically would put
 * "Every source differs" above "Regressed" and quietly contradict the table underneath.
 */
export const WORKLIST_STATES: WorklistState[] = [
  "REGRESSED",
  "REMITS_DISAGREE",
  "VERDICTS_DISAGREE",
  "LEGACY_AND_AI_DISAGREE",
  "LEGACY_DISAGREES",
  "EVERY_SOURCE_DIFFERS",
  "ONLY_LEGACY_DIFFERS",
  "ONLY_AI_DIFFERS",
  "NOT_CONFIGURED",
];

export const WORKLIST_STATE_LABELS: Record<WorklistState, string> = {
  REGRESSED: "Regressed",
  REMITS_DISAGREE: "Remits disagree",
  VERDICTS_DISAGREE: "Human input disagrees",
  LEGACY_AND_AI_DISAGREE: "Legacy + AI disagree",
  /**
   * 🚨 "(AI NOT ASKED)" IS LOAD-BEARING, NOT A PARENTHETICAL. Both this State and the one above mean
   * the same thing about the values — legacy and the engine disagree, with the engine the odd one out
   * — and differ ONLY in whether the AI was asked in that cluster (`worklistStateOf`). Labelled plain
   * "Legacy disagrees" it read as the general case, so an empty filter looked like a contradiction of
   * an agreement card saying Engine + Legacy match 93.7% of the time. As AI coverage completes, this
   * State empties out permanently, and the label should say why.
   */
  LEGACY_DISAGREES: "Legacy disagrees (AI not asked)",
  EVERY_SOURCE_DIFFERS: "Every source differs",
  ONLY_LEGACY_DIFFERS: "Only legacy differs",
  ONLY_AI_DIFFERS: "Only the AI differs",
  NOT_CONFIGURED: "Not configured",
};

/** PrimeReact `Tag` severities. A regression is the only red thing on the screen. */
export const WORKLIST_STATE_SEVERITY: Record<
  WorklistState,
  "danger" | "warning" | "info" | undefined
> = {
  REGRESSED: "danger",
  REMITS_DISAGREE: "warning",
  VERDICTS_DISAGREE: "warning",
  LEGACY_AND_AI_DISAGREE: "info",
  LEGACY_DISAGREES: "info",
  EVERY_SOURCE_DIFFERS: "info",
  // 🚨 NEUTRAL, NOT "info": nothing says the engine is wrong in these two — the third selector agrees
  // with it. They are listed so the engine can be compared against each source, not to be fixed.
  ONLY_LEGACY_DIFFERS: undefined,
  ONLY_AI_DIFFERS: undefined,
  NOT_CONFIGURED: undefined,
};

/** One "$250 Remit" line: the value, and separately who says it so the cell can grey the source. */
export interface IOtherSource {
  /** Stable React key. Not shown. */
  key: string;
  value: string;
  source: string;
  /**
   * Whether the engine differs from this source ANYWHERE in the cluster, read from
   * `engineDisagreesWith` — the one per-source answer on the row.
   *
   * 🚨 A SOURCE CAN HAVE A VALUE HERE AND NOT DIFFER. `legacyMin`/`aiMin` are ranges over the
   * cluster's disagreeing checks, which includes the ones where THE OTHER selector was the odd one
   * out — so on an `ONLY_AI_DIFFERS` cluster legacy carries the engine's own value. Marking every
   * listed source as a disagreement would be wrong that often.
   */
  differs: boolean;
}

/**
 * "$250 Remit" / "$1,900 Legacy, AI" — the value AND who says it, because "other sources say" with
 * no source named is the thing this redesign exists to stop.
 *
 * 🚨 EVERY SOURCE THAT HAS A VALUE, ALWAYS. This used to return on the first truthy thing it found:
 * where a remit existed the cell showed the remit ALONE and legacy and the AI were never rendered at
 * all. That is precisely the row a "Legacy disagrees" filter selects, so the one column that could
 * say what legacy reads was guaranteed to be silent exactly when someone had asked for it — a row
 * reading `Engine ≠ Legacy, AI, Remit` above a cell showing only a remit looks like a broken filter.
 *
 * 🚨 The value and source come back SEPARATELY because the mockups set the source in grey beside the
 * value: the value is the reading, the source is the provenance, and running them together as one
 * string made the cell read as a single long label.
 */
export const otherSourcesText = (row: IWorklistRow): IOtherSource[] => {
  if (row.benefit === null) return [];
  const differs = (source: EngineDisagreement) =>
    row.engineDisagreesWith.includes(source);

  const legacy = rangeText(row.legacyMin, row.legacyMax, row.benefit);
  const ai = rangeText(row.aiMin, row.aiMax, row.benefit);
  const lines: IOtherSource[] = [];

  /*
   * Legacy and the AI collapse to one line ONLY when they read the same value AND the engine stands
   * in the same relation to both — otherwise "Legacy, AI" would carry a single ≠ marker for two
   * sources it is not equally true of.
   */
  if (legacy && ai && legacy === ai && differs("legacy") === differs("ai")) {
    lines.push({
      key: "legacy,ai",
      value: legacy,
      source: "Legacy, AI",
      differs: differs("legacy"),
    });
  } else {
    if (legacy)
      lines.push({
        key: "legacy",
        value: legacy,
        source: "Legacy",
        differs: differs("legacy"),
      });
    if (ai)
      lines.push({
        key: "ai",
        value: ai,
        source: "AI",
        differs: differs("ai"),
      });
  }

  /*
   * 🚨 ONE LINE FOR BOTH TRUTH SOURCES. There is a single `confirmedMin`/`confirmedMax` range behind
   * a remit and a human verdict, so they cannot be split into two values without inventing one.
   * `confirmedBy` names whichever of them contributed.
   */
  const confirmed = rangeText(row.confirmedMin, row.confirmedMax, row.benefit);
  const sources = [
    row.confirmedBy.remit ? "Remit" : null,
    row.confirmedBy.verdict ? "Human input" : null,
  ].filter((each): each is string => each !== null);
  if (confirmed && sources.length > 0)
    lines.push({
      key: "confirmed",
      value: confirmed,
      source: sources.join(", "),
      differs: differs("remit") || differs("verdict"),
    });

  return lines;
};

/**
 * The AI's confidence as a word, because a reader acts on "is this worth checking", not on 0.57.
 *
 * 🚨 THE THRESHOLDS ARE MEASURED, NOT PICKED FOR ROUND NUMBERS. Match rate against remits on prod
 * (2026-09-18, 2,506 scored AI picks) rises with confidence and breaks twice:
 *
 *   under 0.5   52-78%   → low
 *   0.5 to 0.7  85-87%   → med
 *   0.7 and up  93-96%   → high
 *
 * Re-measure before moving them. The query groups `RemitAccuracyScore` by `width_bucket` of the
 * pick's confidence; note a miss is `ENGINE_DIFFERENT`/`ENGINE_ZERO`/`ENGINE_NO_VALUE` — there is no
 * `MISS` outcome, and filtering for one silently reports every band as 100%.
 */
export const CONFIDENCE_MED_FROM = 0.5;
export const CONFIDENCE_HIGH_FROM = 0.7;

export const confidenceBand = (
  confidence: number | null | undefined
): "high" | "med" | "low" | null => {
  if (confidence === null || confidence === undefined) return null;
  if (confidence >= CONFIDENCE_HIGH_FROM) return "high";
  if (confidence >= CONFIDENCE_MED_FROM) return "med";
  return "low";
};

/** "2 of 3" — recorded of the checks in this cluster that could carry a verdict. */
export const verdictsText = (row: IWorklistRow) =>
  row.verdictsNeeded === 0
    ? "—"
    : `${row.verdictsRecorded} of ${row.verdictsNeeded}`;

/**
 * One headline percentage, never without its sample size.
 *
 * 🚨 SCORED AGAINST REMITS AND HUMAN VERDICTS ONLY, on the checks that have one. Legacy and the AI
 * are never scored against, because agreement with legacy measures agreement with the thing being
 * replaced.
 */
export const accuracyPercent = (matches: number, verified: number) =>
  verified === 0 ? null : (100 * matches) / verified;

export const accuracyText = (matches: number, verified: number) => {
  const percent = accuracyPercent(matches, verified);
  return percent === null ? "—%" : `${percent.toFixed(1)}%`;
};

export const ENGINE_ACCURACY_TOOLTIP =
  "How often the engine’s value matched the remit or human input on the eligibility checks that have one. Legacy and the AI are not included in this calculation. Each benefit is split by what the remit or human input says the patient was CHARGED: “Charged” counts only the checks where the patient owed something, “No charge” the ones where they owed nothing. The split is on the verified value, never on the engine’s.";

/**
 * The two halves of every benefit row on the Engine accuracy card.
 *
 * 🚨 THE COLUMN HEADINGS ARE THE WHOLE POINT. One percentage per benefit conflated "the engine gets
 * the nothing-owed case right" with "the engine gets a real charge right", and the first is the
 * bigger half everywhere: measured on prod 2026-09-22, physician copay read 26.7% overall — 47.2%
 * where nothing was charged, and 0 of 111 where something was. A reader has to be able to see which
 * number is which without hovering anything, so both are labelled in place rather than behind the
 * tooltip.
 *
 * 🚨 NO "$0" AND NO "0%" IN EITHER HEADING. One heading spans all three benefit rows, and co-insurance
 * is a PERCENT while the two copays are dollars — "Charged $0" above a co-insurance row is a category
 * error, since the not-charged case there is 0%, not $0. It is latent only because remit-derived
 * co-insurance truth can never be zero (`remitTruth.ts` drops a line with `pr2 <= 0` rather than
 * recording 0%); the first 0% co-insurance VERDICT a reviewer records makes it visible.
 *
 * 🚨 NEITHER LABEL MAY BE A SUBSTRING OF THE OTHER, in any casing. "Charged $0" contained "Charged",
 * which is exactly why a `toHaveTextContent` assertion passed with the two headings swapped — the one
 * mistake that would make this card actively misleading. The same trap rules out "Nothing charged".
 * `BenefitComparisonTab.test.tsx` asserts this pair by exact `textContent`, not by substring.
 */
export const ACCURACY_SPLIT_LABELS = {
  charged: "Charged",
  zero: "No charge",
} as const;

/**
 * "0 / 111", or "no cases" for a half with nothing in it.
 *
 * 🚨 NEVER A PERCENTAGE WITHOUT ITS SAMPLE SIZE, and the split makes that sharper: the two halves of
 * one benefit routinely differ by a factor of ten in size, so "98.3%" and "78.5%" side by side are
 * not comparable until the reader can see they were measured over 1,216 checks and 284.
 */
export const accuracySampleText = (matches: number, verified: number) =>
  verified === 0
    ? "no cases"
    : `${matches.toLocaleString()} / ${verified.toLocaleString()}`;

// ---------------------------------------------------------------------------
// How often the three selectors agree
// ---------------------------------------------------------------------------

/**
 * 🚨 AGREEMENT IS NOT ACCURACY, and the caption has to keep saying so. Nothing here tells you which
 * side was RIGHT — three selectors agreeing on the wrong value agree just as loudly. It is worth a
 * card anyway for the two benefits the Engine accuracy card must leave blank: a remit says what the
 * patient owed on one claim, never what deductible or out-of-pocket they had REMAINING at check time,
 * so those two have no truth source at all and this is the only signal they have.
 */
export const AGREEMENT_HEADING = "How often the three selectors agree";

/**
 * 🚨 THE CAVEATS LIVE IN THE TOOLTIP, NOT IN A PARAGRAPH ABOVE THE CARDS. They used to be an intro
 * line stating all three at once — compared-only, overlapping rows, agreement-is-not-accuracy — which
 * is a wall of text over a row of cards that are meant to be read at a glance. The rows still carry
 * the meaning; the tooltip carries the fine print for whoever wants it.
 */
export const AGREEMENT_TOOLTIP =
  "Of the checks where Legacy, Engine and the AI all picked a value. A pair's row counts every check those two landed on the same value, including the ones all three agreed on — so the rows overlap and do not add up to 100%. Agreement is not accuracy: it says nothing about which one is right.";

/**
 * One row of a card: how often those sources landed on the same value.
 *
 * 🚨 THE PAIR ROWS ARE INCLUSIVE, AND THE INTRO SAYS SO. They count every check the two agreed on,
 * the all-three ones included, because the question they answer is "how close are these two to each
 * other" — and a pair rate of 7.3% next to an all-three rate of 90.5% reads as "Legacy and Engine
 * barely agree" when the truth is that they agree 97.8% of the time. The exclusive split is still
 * recoverable by subtracting `allThree`, and `None agree` is unchanged either way.
 *
 * 🚨 SO THE ROWS DO NOT SUM TO 100% and must never be drawn as a stacked bar or a pie. Two pairs can
 * only both be true when all three agree — if legacy = engine and legacy = AI then engine = AI — so
 * the overlap between any two pair rows is exactly `allThree`.
 */
export interface IAgreementRow {
  key: "allThree" | "legacyAndEngine" | "legacyAndAi" | "engineAndAi" | "none";
  label: string;
  count: number;
}

/**
 * 🚨 THE THREE PAIRS FIRST, THEN THE TWO TOTALS. The card is read to compare the pairs against one
 * another — that is what "how do these selectors relate" means — and the engine's two pairings lead
 * because the engine is the thing being judged. `All three` and `None agree` are the bounds those
 * three rates sit between, so they close the card rather than interrupting it.
 */
export const agreementRows = (cell: ISourceAgreementCell): IAgreementRow[] => [
  {
    key: "legacyAndEngine",
    label: "Engine + Legacy",
    count: cell.allThree + cell.legacyAndEngine,
  },
  {
    key: "engineAndAi",
    label: "Engine + AI",
    count: cell.allThree + cell.engineAndAi,
  },
  {
    key: "legacyAndAi",
    label: "Legacy + AI",
    count: cell.allThree + cell.legacyAndAi,
  },
  { key: "allThree", label: "All three", count: cell.allThree },
  { key: "none", label: "None agree", count: cell.none },
];

export const agreementPercent = (count: number, compared: number) =>
  compared === 0 ? null : (100 * count) / compared;

export const agreementText = (count: number, compared: number) => {
  const percent = agreementPercent(count, compared);
  return percent === null ? "—%" : `${percent.toFixed(1)}%`;
};

/** 🚨 Never a percentage without its sample size — the rule the whole screen is built on. */
export const agreementSampleText = (compared: number) =>
  compared === 0
    ? "No checks have all three yet"
    : `${compared.toLocaleString()} compared`;

/**
 * THE FILTER THE SCREEN IS FOR: which source does the engine disagree with.
 *
 * 🚨 NOT MUTUALLY EXCLUSIVE, UNLIKE STATE. A cluster where legacy AND the AI both differ from the
 * engine appears under either option, and a cluster whose State reads "Remits disagree" still appears
 * under "Legacy" if legacy differs on any of its checks. Filtering on `state` cannot do that — a
 * cluster has one State, the first rule that matched.
 */
export const ENGINE_DISAGREEMENT_FILTERS: {
  value: EngineDisagreement;
  label: string;
}[] = [
  { value: "legacy", label: "Legacy disagrees" },
  { value: "ai", label: "AI disagrees" },
  { value: "remit", label: "Remits disagree" },
  { value: "verdict", label: "Human input disagrees" },
];

/** The tags on a row: every source the engine differs from, in the filter's order. */
export const ENGINE_DISAGREEMENT_LABELS: Record<EngineDisagreement, string> = {
  legacy: "Legacy",
  ai: "AI",
  remit: "Remit",
  verdict: "Human input",
};

export const ENGINE_DISAGREEMENT_PLACEHOLDER = "Any source";

export const OTHER_SOURCES_TOOLTIP =
  "Every source that read a value on this cluster's disagreeing checks. ≠ marks a source the engine differs from somewhere in the cluster; a greyed value is one the engine matches. A remit and a human verdict share one line because they share one recorded value.";

/**
 * What an empty worklist says.
 *
 * 🚨 THE UNFILTERED MESSAGE IS A CLAIM, AND IT WAS BEING MADE WHEN IT WAS FALSE. "Today's engine
 * agrees with every remit, verdict, legacy and AI value" is true of an empty UNFILTERED list. Shown
 * under a filter that simply has no clusters it asserts agreement that does not exist — and it did,
 * directly under cards reporting a 6.3% disagreement.
 */
export const worklistEmptyMessage = (filtered: boolean) =>
  filtered
    ? "No clusters match this filter. The cards above are all-time and are not narrowed by it."
    : "Nothing to fix: today’s engine agrees with every remit, verdict, legacy and AI value.";

export const WORKLIST_INTRO =
  "Clusters where the engine disagrees with a remit, human input, legacy or the AI.";

export const COVERAGE_INTRO = (checks: number) =>
  `Of all ${checks.toLocaleString()} eligibility checks, how much data we have from each`;

/**
 * 🚨 NO HARD LINE BREAK. One was tried after "used" to even up the two lines, and it wrecked the
 * card at narrow widths — a forced break plus wrapping gives three ragged lines. It wraps naturally
 * and is centred instead.
 */
export const REGRESSED_CARD_TEXT =
  "Checks with a remit or human input that used to match and no longer do.";

/** "84.0% → 89.5% over the last 30 runs". 🚨 The caption states the range AND the window. */
export const trendCaption = (
  first: number | null,
  last: number | null,
  runs: number
) =>
  first === null || last === null || runs === 0
    ? "No nightly runs recorded yet."
    : `${first.toFixed(1)}% → ${last.toFixed(1)}% over the last ${runs} ${
        runs === 1 ? "run" : "runs"
      }`;

// ---------------------------------------------------------------------------
// Inside a cluster
// ---------------------------------------------------------------------------

export const CHECK_STATUS_LABELS: Record<CheckStatus, string> = {
  OPEN: "Open",
  MATCHING: "Matching",
  FIXED: "Fixed",
  REGRESSED: "Regressed",
  PENDING_RERUN: "Pending re-run",
};

export const CHECK_STATUS_SEVERITY: Record<
  CheckStatus,
  "danger" | "warning" | "success" | "info" | undefined
> = {
  OPEN: "warning",
  MATCHING: "success",
  FIXED: "success",
  REGRESSED: "danger",
  PENDING_RERUN: "warning",
};

/** A check nothing has confirmed a value for. The only rows that carry a Review button. */
export const NEEDS_A_VERDICT = "Needs human input";

export const RERUN_BUTTON_LABEL = "Re-run engine on these checks";

/**
 * 🚨 WHAT THE RE-RUN DOES AND DOES NOT DO, said on screen rather than left to be assumed — because
 * "re-run the engine" reads like something that could move a patient's estimate, and it cannot.
 */
// ---------------------------------------------------------------------------
// Recording a human verdict
// ---------------------------------------------------------------------------

/**
 * 🚨 THE FIXED QUESTION, SHOWN ON SCREEN. Every reviewer answers this one, so two reviewers — and the
 * same reviewer on two days — are judging the same thing. It must match the API's `verdictQuestion`.
 *
 * 🚨 IT NAMES THE BENEFIT. A reviewer reading one row of a five-benefit cluster should not have to
 * work out which benefit is being asked about.
 */
export const verdictQuestion = (benefit: ComparisonBenefit) =>
  `Review the pVerify response and select the correct value that should have been selected for ${COMPARISON_BENEFIT_LABELS[
    benefit
  ].toLowerCase()}.`;

export const SIDE_LABELS: Record<"legacy" | "engine" | "ai", string> = {
  legacy: "Legacy",
  engine: "Engine",
  ai: "AI",
};

/**
 * 🚨 ONE COLOUR PER SOURCE, DEFINED ONCE AND USED EVERYWHERE ON THIS SCREEN — the response table's
 * tags, the verdict options, the recorded human input. They used to be set per component and the
 * engine and a remit both came out orange, which read as though the two were the same kind of thing.
 * They are opposites: one is what we computed, the other is what the payer actually paid.
 *
 *   ORANGE  a source of truth — a remit, or human input. Also the States they cause.
 *   GREEN   the engine, the thing under test.
 *   GREY    legacy, the old system being replaced.
 *   BLUE    the AI.
 *   RED     a regression, and a remit/human-input conflict. Nothing else is ever red.
 *
 * PrimeReact 8 has only four severities, so grey is a class rather than one of them. 🚨 It is a LIGHT
 * grey with near-black text: legacy appears on nearly every row, and a dark chip that often turned
 * out to be the least interesting thing there pulled the eye first.
 */
export const NEUTRAL_TAG_CLASS = "bg-gray-200 text-900";
export interface ISourceTag {
  severity?: "success" | "info" | "warning" | "danger";
  className?: string;
}

export const SOURCE_TAGS = {
  engine: { severity: "success" },
  legacy: { className: NEUTRAL_TAG_CLASS },
  ai: { severity: "info" },
  remit: { severity: "warning" },
  human: { severity: "warning" },
} as const satisfies Record<string, ISourceTag>;

/**
 * The sources a row can name, as chips — THE SAME CHIPS THE RESPONSE PANEL USES.
 *
 * 🚨 ONE VOCABULARY FOR THE WHOLE SCREEN. A reader who has learnt in the response panel that grey is
 * legacy, blue is the AI and orange is a source of truth should not have to learn a second rendering
 * of the same four words in the worklist. `verdict` takes the `human` chip because that is what the
 * panel calls the same thing.
 */
export const ENGINE_DISAGREEMENT_TAGS: Record<EngineDisagreement, ISourceTag> =
  {
    legacy: SOURCE_TAGS.legacy,
    ai: SOURCE_TAGS.ai,
    remit: SOURCE_TAGS.remit,
    verdict: SOURCE_TAGS.human,
  };

/** The three benefit selectors, for the verdict options and the agreement row. */
export const SIDE_TAGS: Record<"legacy" | "engine" | "ai", ISourceTag> = {
  legacy: SOURCE_TAGS.legacy,
  engine: SOURCE_TAGS.engine,
  ai: SOURCE_TAGS.ai,
};

/** `"$75"` with `"Legacy, Engine"` beside it. `$0` and "nothing reported" are ONE option. */
export const verdictOptionValueLabel = (
  option: IVerdictOption,
  benefit: ComparisonBenefit
) =>
  option.value === 0 && option.fromNoValue
    ? `${formatComparisonValue(0, benefit)} / no value`
    : formatComparisonValue(option.value, benefit);

export type IVerdictOptionSource = "legacy" | "engine" | "ai" | "remit";

export const VERDICT_SOURCE_LABELS: Record<IVerdictOptionSource, string> = {
  ...SIDE_LABELS,
  remit: "Remit",
};

export const VERDICT_SOURCE_TAGS: Record<IVerdictOptionSource, ISourceTag> = {
  ...SIDE_TAGS,
  remit: SOURCE_TAGS.remit,
};

/**
 * Who said this value, selectors first and the remit last — the order the buttons are built in.
 *
 * 🚨 THE REMIT BELONGS HERE EVEN THOUGH IT IS NOT A SELECTOR. On a "Remits disagree" cluster it is
 * the value none of the three offer, and leaving it out left the reviewer retyping a number from the
 * row directly above into "Another value". It is last, and never pre-selected: the question asks what
 * the RESPONSE should have yielded, and a reviewer who reads the response differently should be able
 * to say so — that disagreement is recorded as a conflict, not suppressed.
 */
export const verdictOptionSources = (
  option: IVerdictOption
): IVerdictOptionSource[] => [
  ...option.sides,
  ...(option.fromRemit ? (["remit"] as const) : []),
];

export const verdictOptionSourcesLabel = (option: IVerdictOption) =>
  verdictOptionSources(option)
    .map((source) => VERDICT_SOURCE_LABELS[source])
    .join(", ");

export const ANOTHER_VALUE_LABEL = "Another value";

/**
 * 🚨 A REAL ANSWER, NOT AN ESCAPE HATCH, and it sits in the same radio group as the values for
 * exactly that reason: a response that does not answer the question is a finding about the response.
 */
export const CANNOT_DETERMINE_LABEL = "Can’t tell from this response";

export const RECORD_VERDICT_LABEL = "Record human input";

/**
 * THE ONE-SENTENCE CONSEQUENCE, shown before anything is written, because "selecting is not
 * submitting" is only true if the person can see what submitting will do.
 */
export const verdictConsequence = (
  chosen: string | null,
  hasExistingVerdict: boolean
) => {
  if (chosen === null)
    return "Select the value the estimate should have quoted. Nothing is written until you record it.";
  if (hasExistingVerdict)
    return `Recording ${chosen} replaces the verdict on this check and re-seeds it — correcting yourself never counts as a regression.`;
  return `Recording ${chosen} adds this check to the set the engine is scored against, from the next run.`;
};

/** "Agrees with the human input now: Legacy, AI" — derived at display time, never stored. */
export const AGREES_WITH_LABEL = "Agrees with the human input now";

export const VERDICT_TYPE_LABELS = {
  VALUE: "Correct value",
  CANNOT_DETERMINE: "Can’t tell from this response",
} as const;

/** "Ani · 2026-09-17 · changed 1×" */
export const verdictByline = (verdict: IBenefitVerdict) =>
  `${verdict.reviewedByName ?? "Unknown reviewer"} · ${verdict.recordedAt.slice(
    0,
    10
  )}${verdict.revisions > 1 ? ` · changed ${verdict.revisions - 1}×` : ""}`;

// ---------------------------------------------------------------------------
// CLE-2821 — verified checks, scored against today's engine
// ---------------------------------------------------------------------------

export const VERIFICATION_STATUS_LABELS: Record<VerificationStatus, string> = {
  OPEN: "Open",
  FIXED: "Fixed",
  REGRESSED: "Regressed",
  MATCHING: "Matching",
};

export const TRUTH_SOURCE_LABELS: Record<TruthSource, string> = {
  VERDICT: "Human input",
  REMIT: "Remit",
  BOTH: "Remit + human input",
};

/** "8 / 10 (80.0%)" — never a percentage without its sample size; "none verified" at zero. */
export const verifiedCellText = (cell: IVerificationCell) =>
  cell.verified === 0
    ? "none verified"
    : `${cell.matches} / ${cell.verified} (${accuracyText(
        cell.matches,
        cell.verified
      )})`;

/** 🚨 The one sentence the scorecard exists to say, and never without its sample size. */
export const verifiedHeadlineText = (totals: IVerificationTotals) =>
  totals.verified === 0
    ? "No verified checks yet. A check joins this set when somebody records human input on it, or a remit gives it a value."
    : `Today’s engine matches ${totals.matches.toLocaleString()} of ${totals.verified.toLocaleString()} verified checks`;

// ---------------------------------------------------------------------------
// CLE-2820 follow-up — how old the numbers are
// ---------------------------------------------------------------------------

/**
 * 🚨 THE ONE SENTENCE THAT KEEPS THIS SCREEN HONEST. The worklist's numbers come from a cached
 * aggregate rebuilt when the ENGINE's answers change — so between somebody saving an account's
 * configuration and the re-score landing, what is on screen is the PREVIOUS configuration's
 * numbers. That used to be invisible, and the person in that window is the person who just made the
 * change and came back to see its effect.
 *
 * It names the account, because "some accounts" would send the reader to check all of them.
 */
export const pendingConfigText = (
  accounts: IPendingConfigAccount[],
  now: Date = new Date()
) => {
  if (accounts.length === 0) return null;
  const [first, ...rest] = accounts;
  const others = rest.length > 0 ? ` and ${rest.length} more` : "";
  return `Re-running the engine for ${
    first.accountName
  }${others} — the numbers below are from the previous configuration (saved ${relativeTime(
    first.configChangedAt,
    now
  )}).`;
};

/** Shown while a rebuild is actually running, which is seconds. */
export const REBUILDING_TEXT = "Recomputing the worklist…";

/** The quiet case: nothing pending, nothing rebuilding. "as of 3 minutes ago". */
export const computedAtText = (
  computedAt: string | null,
  now: Date = new Date()
) => (computedAt === null ? null : `as of ${relativeTime(computedAt, now)}`);

/**
 * How often to ask again WHILE something is pending or rebuilding — and never otherwise. This
 * screen is all-time and nothing on it moves while somebody reads it, so a standing poll would be
 * a full worklist read every few seconds for nothing.
 */
export const FRESHNESS_POLL_MS = 5_000;
